import { useId, useState, type ChangeEvent } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { ChevronDown, ChevronUp, Filter } from "react-feather";

import styles from "./BetsFilters.module.css";
import { Button, Collapse, Input, Select } from "../../../shared";
import type { BetFilterOption } from "../../../../queries/user-manager/useGetBetFilterDropdowns";
import { AppStrings } from "../../../../utils/constants/AppStrings";

export type BetsFiltersProps = {
  // Dropdown options
  statusOptions: BetFilterOption[];
  currencyOptions: BetFilterOption[];
  betTypeOptions: BetFilterOption[];

  // Draft state — what's selected pre-Apply.
  status: string[];
  moneyType: string;
  betTypes: string[];
  searchValue: string;

  // Handlers
  onStatusChange: (v: string[] | undefined) => void;
  onMoneyTypeChange: (v: string | undefined) => void;
  onBetTypesChange: (v: string[] | undefined) => void;
  onSearchValueChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onSearchPressed: () => void;
  onApply: () => void;
  onClear: () => void;

  /** Drives the Clear button's disabled state. */
  hasAppliedOrDraftFilters: boolean;
};

const BetsFilters = ({
  statusOptions,
  currencyOptions,
  betTypeOptions,
  status,
  moneyType,
  betTypes,
  searchValue,
  onStatusChange,
  onMoneyTypeChange,
  onBetTypesChange,
  onSearchValueChange,
  onSearchPressed,
  onApply,
  onClear,
  hasAppliedOrDraftFilters,
}: BetsFiltersProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const panelId = useId();

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerRow}>
        <p className={styles.headerText}>{AppStrings.USER_SPORTS_BETS}</p>

        <div className={styles.headerActions}>
          <Input
            variant="search"
            placeholder={AppStrings.SEARCH_BY_BET_ID}
            className={styles.betSearchInput}
            value={searchValue}
            onChange={onSearchValueChange}
            data-testid="bets-search-input"
          />

          <Button
            size="large"
            onClick={onSearchPressed}
            data-testid="bets-search-button"
          >
            <SearchOutlined
              style={{
                fontSize: "20px",
                color: "var(--COLOR-LIGHT_TEXT_COLOR)",
              }}
            />
          </Button>

          <Button
            intent="primary"
            size="large"
            onClick={() => setOpen((prev) => !prev)}
            aria-expanded={open}
            aria-controls={panelId}
            data-testid="bets-filters-toggle"
          >
            <span className={styles.filterToggleInner}>
              <Filter size={18} />
              {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </span>
          </Button>
        </div>
      </div>

      <Collapse open={open} className={styles.collapseHost}>
        <div
          id={panelId}
          className={styles.filtersPanel}
          data-testid="bets-filters-panel"
        >
          <Select
            allowClear
            mode="multiple"
            className={styles.compactFilter}
            options={betTypeOptions}
            value={betTypes}
            onChange={onBetTypesChange}
            placeholder={AppStrings.BET_TYPE}
            data-testid="bets-type-select"
          />

          <Select
            allowClear
            mode="multiple"
            className={styles.compactFilter}
            options={statusOptions}
            value={status}
            onChange={onStatusChange}
            placeholder={AppStrings.STATUS}
            data-testid="bets-status-filter"
          />

          <Select
            allowClear
            className={styles.compactFilter}
            options={currencyOptions}
            value={moneyType || undefined}
            onChange={onMoneyTypeChange}
            placeholder={AppStrings.CURRENCY}
            data-testid="bets-money-type-filter"
          />

          <Button
            size="large"
            onClick={onApply}
            data-testid="bets-apply-filters"
          >
            {AppStrings.APPLY}
          </Button>

          <Button
            variant="ghost"
            size="large"
            onClick={onClear}
            disabled={!hasAppliedOrDraftFilters}
            data-testid="bets-clear-filters"
          >
            {AppStrings.CLEAR_FILTERS}
          </Button>
        </div>
      </Collapse>
    </div>
  );
};

export default BetsFilters;
